// ✅ src/pages/TarMixing/ProductionListing.jsx
// Updated with safe localStorage handling + common headers

import React, { useState, useContext, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const ProductionListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;
  const navigate = useNavigate();

  // ✅ Safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchid = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const userid = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchid,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userid,
    }),
    [finYearId, finYear, branchid, branchType, userid]
  );

  const [showFilters, setShowFilters] = useState(true);

  const defaultFormData = {
    fromDate: "01/01/2021",
    toDate: "11/12/2025",
  };

  const [formData, setFormData] = useState(defaultFormData);

  const [productionData, setProductionData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ API returns: productionid
  const getRowId = (row) => {
    const id = Number(row?.productionid);
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  const validRowIds = useMemo(() => {
    return (productionData || []).map(getRowId).filter((x) => x != null);
  }, [productionData]);

  // ✅ Total quantity for Results Summary panel
  const totalQuantity = useMemo(
    () => productionData.reduce((sum, r) => sum + (Number(r.Quantity) || 0), 0),
    [productionData]
  );

  // -------------------------
  // Checkbox selection
  // -------------------------
  const toggleRow = (id) => {
    if (id == null) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allSelected =
    validRowIds.length > 0 && selectedIds.length === validRowIds.length;

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(validRowIds);
  };

  // -------------------------
  // Search
  // -------------------------
  const handleSearch = async () => {
    if (!branchid) return toast.error("BranchId missing!");
    if (!formData.fromDate || !formData.toDate)
      return toast.warn("From Date and To Date required");

    try {
      setLoading(true);
      setHasSearched(true);

      const res = await axios.get(`${BASE_URL}/api/productionListing/search`, {
        params: {
          branchid,
          userid,
          fromDate: formData.fromDate,
          toDate: formData.toDate,
        },
        headers: commonHeaders,
      });

      const rows = res.data?.data || [];
      console.log("res", rows);
      setProductionData(rows);
      setSelectedIds([]);

      if (rows.length === 0) toast.info("No Records found");
    } catch (err) {
      console.error("Search error:", err);
      toast.error(err?.response?.data?.message || "Search failed");
      setProductionData([]);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Clear filters back to defaults and reload
  const handleClearFilters = () => {
    setFormData(defaultFormData);
    setTimeout(() => handleSearch(), 0);
  };

  // -------------------------
  // Add new
  // -------------------------
  const handleAddNew = () => {
    navigate("/TarMixing/Tar_Production_Entry");
  };

  // -------------------------
  // ✅ Update — same navigation logic, now triggered per-row via Edit icon
  // -------------------------
  const handleUpdateRow = (row) => {
    const id = getRowId(row);

    if (!id) {
      toast.error("Invalid record");
      return;
    }

    navigate("/TarMixing/Tar_Production_Entry", {
      state: {
        editMode: true,
        id,
        rowData: row,
      },
    });
  };

  // -------------------------
  // Delete selected (bulk, via Remove button)
  // -------------------------
  const handleDelete = async () => {
    if (selectedIds.length === 0)
      return toast.warn("Select at least one row to delete.");

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      const res = await axios.delete(
        `${BASE_URL}/api/productionListing/deleteSelected`,
        {
          params: { branchid, userid },
          data: { ids: selectedIds },
          headers: commonHeaders,
        }
      );

      if (res.data?.success === false) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success(res.data?.message || "Deleted successfully!");

      setProductionData((prev) =>
        prev.filter((r) => !selectedIds.includes(getRowId(r)))
      );
      setSelectedIds([]);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Production Listing
            </h1>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus /> Add New
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch /> {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={formData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={formData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading && productionData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <span className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</span>
              </div>
            ) : productionData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <span className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </span>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Item Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Quantity</th>

                      {/* EDIT - SECOND LAST COLUMN */}
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>

                      {/* SELECT ALL - LAST COLUMN */}
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {productionData.map((row, idx) => {
                      const id = getRowId(row);
                      const checked = selectedIds.includes(id);

                      return (
                        <tr
                          key={id || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark ? "bg-gray-800" : "bg-white"
                              : isDark ? "bg-gray-750" : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {idx + 1}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.fld_ProductionDate
                              ? new Date(row.fld_ProductionDate).toLocaleDateString("en-GB")
                              : "-"}
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                            {row.ProductionName || "-"}
                          </td>
                          <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                            {row.Quantity ?? 0}
                          </td>

                          {/* EDIT */}
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleUpdateRow(row)}
                              className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                              title="Edit"
                            >
                              ✏️
                            </button>
                          </td>

                          {/* SELECT */}
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* ==================== REMOVE BUTTON ==================== */}
                <div className="p-4">
                  <button
                    onClick={handleDelete}
                    disabled={loading || selectedIds.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      loading || selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                    } bg-red-600 hover:bg-red-700 text-white`}
                  >
                    🗑
                    Remove
                  </button>
                </div>
              </>
            )}
          </div>



          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default ProductionListing;